<template>
  <content-subpage
    class="status-history-content-subpage"
    :visible="visible"
    :titles="titles"
    :has-parent-subpage="hasParentSubpage"
    :back-label="backLabel"
    :show-content-title="false"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <template #main-content>
      <overlay-loader v-if="isLoading" />
      <div v-else class="status-history-wrapper">
        <!-- Update status form -->
        <status-history-update-form
          v-if="showUpdateStatusForm"
          :resource-uuid="resourceUuid"
          :resource-domain="resourceDomain"
          :user-display-name="userDisplayName"
          :new-status="newStatus"
          :lock-service="lockService"
          :save-action="saveAction"
          :post-success-state-action="postSuccessStateAction"
          @cancel="handleCancelStatusUpdate"
        />

        <!-- Status history listing -->
        <status-history-listing
          :resource-domain="resourceDomain"
          :records="statusHistoryRecords"
          :viewing-status="resourceStatus"
        />
      </div>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { isEmpty } from "@utils/var.check";
import statusHistoryApi from "@apis/status.history.api";
import StatusHistoryListing from "@plugins/status-history/components/StatusHistoryListing.vue";
import StatusHistoryUpdateForm from "@plugins/status-history/components/StatusHistoryUpdateForm.vue";
import { RESOURCE_TYPE } from "@plugins/common/constant/resource.constant";
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";

// Props
const props = defineProps({
  params: { type: Object, required: true },
});

// Data
const isLoading = ref(false);
const statusHistoryRecords = ref([]);
const parentSubpageName = ref("");

const {
  lockService,
  saveAction,
  postSuccessStateAction,
  newStatus,
  resourceDomain,
  resourceType,
  resourceUuid,
  resourceStatus,
  userDisplayName,
} = props.params;

const { t } = useI18n();
const { titles, actionBack, actionHidden, hasParentSubpage, visible, defaultExpose } =
  useContentSubpage(props.params);
const { passiveApiFailHandler } = useErrorHandler();

// Computed
const showUpdateStatusForm = computed(() => !isEmpty(props.params.newStatus));

const backLabel = computed(() => {
  return `${t("content_subpage_go_back_link")} ${parentSubpageName.value}`;
});

// Methods
const fetchData = async () => {
  isLoading.value = true;

  // set the correct API service base on the resource domain.
  let fetchStatusHistory;
  if (resourceType === RESOURCE_TYPE.LESSON) {
    fetchStatusHistory = statusHistoryApi.fetchLessonStatusHistory({
      uuid: resourceUuid,
      resourceDomain,
    });
  } else if (resourceType === RESOURCE_TYPE.MEDIA) {
    fetchStatusHistory = statusHistoryApi.fetchMediaStatusHistory(resourceUuid);
  }

  try {
    const response = await fetchStatusHistory;
    if (response.code === "ok") {
      statusHistoryRecords.value = response.data;
    } else {
      passiveApiFailHandler();
    }
  } catch {
    passiveApiFailHandler();
  } finally {
    isLoading.value = false;
  }
};

const handleCancelStatusUpdate = () => {
  actionHidden();
};

onMounted(() => {
  if (hasParentSubpage) {
    parentSubpageName.value = titles.value.slice(-1)[0];
  }
  titles.value.push(t("status_history_header_label"));

  fetchData();
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/typo";

$global-banner-height: 5.5rem;

.status-history-content-subpage {
  &.content-subpage {
    padding-top: 8.5rem;

    .bx--modal-container {
      @include media("tablet-min") {
        max-height: calc(
          (var(--vh, 1vh) * 100) - #{$app-header-height} - #{$content-subpage-padding-offset} -
            #{$global-banner-height}
        );
      }
    }
  }

  .main-header {
    display: none;
  }
  .content {
    background-color: $ui-01;

    .title {
      @include carbon--type-style("productive-heading-02", true);
    }
  }

  .bx--form {
    .bx--text-area {
      background-color: $ui-background;
    }
  }
}
</style>
